import React from "react";
import MeetPageEmptyState from "./MeetPageEmptyState";

const formatCount = (count, singular, plural) => {
  const n = Number(count) || 0;
  return `${n.toLocaleString()} ${n === 1 ? singular : plural}`;
};

const StatTile = ({ label, value, detail }) => (
  <div className="owner-dashboard__meet-stat">
    <span className="owner-dashboard__meet-stat-value">{value}</span>
    <span className="owner-dashboard__meet-stat-label">{label}</span>
    {detail && <span className="owner-dashboard__meet-stat-detail">{detail}</span>}
  </div>
);

const BarList = ({ rows, countLabel = "times" }) => {
  if (!rows?.length) return null;

  return (
    <ul className="owner-dashboard__meet-list">
      {rows.map((row) => (
        <li key={`${row.label}-${row.friendlyLabel}`} className="owner-dashboard__meet-row">
          <div className="owner-dashboard__meet-row-head">
            <span className="owner-dashboard__meet-row-label">
              {row.friendlyLabel || row.label}
            </span>
            <span className="owner-dashboard__meet-row-stats">
              {formatCount(row.count, countLabel.replace(/s$/, ""), countLabel)}
              {row.sharePercent ? ` · ${row.sharePercent}%` : ""}
            </span>
          </div>
          <div className="owner-dashboard__meet-bar-track" aria-hidden="true">
            <span
              className="owner-dashboard__meet-bar-fill"
              style={{ width: `${Math.max(row.sharePercent || 0, 4)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
};

const MeetPageInsights = ({ meetPageInterest, days = 30, showHeader = true }) => {
  const rootClass = showHeader
    ? "owner-dashboard__panel owner-dashboard__meet"
    : "owner-dashboard__meet";

  if (!meetPageInterest || !meetPageInterest.connected) {
    return (
      <article className={rootClass}>
        <MeetPageEmptyState days={days} variant="no-data" />
      </article>
    );
  }

  const {
    headline,
    pageMetrics,
    conversionRatePercent,
    totals,
    registerByLocation,
    sectionsViewed,
    sectionNav,
    topQuestions,
    actions,
    highlights,
    partnerExposure,
  } = meetPageInterest;

  const showPartnerSection =
    partnerExposure &&
    (partnerExposure.localPartnerClicks > 0 ||
      partnerExposure.exploreBardstownViews > 0 ||
      partnerExposure.visitBardstownCtaClicks > 0 ||
      partnerExposure.topSponsors?.length > 0 ||
      partnerExposure.pitchLines?.length > 0);

  return (
    <article className={rootClass}>
      {showHeader && (
        <div className="owner-dashboard__panel-header owner-dashboard__panel-header--compact">
          <div>
            <h3 className="owner-dashboard__panel-title">Bourbon City Spectacular</h3>
            <p className="owner-dashboard__panel-subtitle">
              Meet page performance · last {days} days · separate from class registration
            </p>
          </div>
        </div>
      )}

      <p className="owner-dashboard__meet-intro">
        How the meet landing page is performing — who visited, how far they scrolled,
        and whether they clicked through to register. Use the sponsor section below
        when pitching hotels, restaurants, and partners for next year.
      </p>

      {headline && <p className="owner-dashboard__meet-headline">{headline}</p>}

      <div className="owner-dashboard__meet-stats">
        <StatTile
          label="Page visits"
          value={(totals.pageViews || 0).toLocaleString()}
          detail={
            pageMetrics?.avgTimeOnPage
              ? `${pageMetrics.avgTimeOnPage} avg on page`
              : null
          }
        />
        <StatTile
          label="Register clicks"
          value={(totals.registerClicks || 0).toLocaleString()}
          detail={
            conversionRatePercent !== null
              ? `${conversionRatePercent}% of visitors clicked register`
              : null
          }
        />
        <StatTile
          label="Tracked actions"
          value={(totals.totalActions || 0).toLocaleString()}
          detail="Scrolls, FAQs, contacts, and more"
        />
      </div>

      {pageMetrics && (
        <p className="owner-dashboard__meet-page-metrics">
          {pageMetrics.engagementRatePercent !== null && (
            <>
              {pageMetrics.engagementRatePercent}% engaged with the page
              {pageMetrics.bounceRatePercent !== null
                ? ` · ${pageMetrics.bounceRatePercent}% left quickly`
                : ""}
              .
            </>
          )}
        </p>
      )}

      {highlights?.length > 0 && (
        <ul className="owner-dashboard__meet-highlights">
          {highlights.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}

      {showPartnerSection && (
        <div className="owner-dashboard__meet-sponsor-pitch">
          <h4 className="owner-dashboard__meet-sponsor-pitch-title">
            For next year&apos;s sponsors
          </h4>
          <p className="owner-dashboard__meet-sponsor-pitch-note">
            Real numbers from the meet page you can share with local businesses and
            sponsors when renewing or pitching partnerships.
          </p>

          <div className="owner-dashboard__meet-stats owner-dashboard__meet-stats--partner">
            <StatTile
              label="Explore Bardstown views"
              value={(partnerExposure.exploreBardstownViews || 0).toLocaleString()}
              detail="Families scrolled to hotels & restaurants"
            />
            <StatTile
              label="Plan your visit clicks"
              value={(partnerExposure.visitBardstownCtaClicks || 0).toLocaleString()}
              detail="Opened Visit Bardstown tourism site"
            />
            <StatTile
              label="Local listing clicks"
              value={(partnerExposure.partnerWebsiteClicks || 0).toLocaleString()}
              detail="Hotel & restaurant website visits"
            />
            <StatTile
              label="Directions requests"
              value={(partnerExposure.partnerDirectionsClicks || 0).toLocaleString()}
              detail="Opened maps for a listed business"
            />
            <StatTile
              label="Sponsor logo clicks"
              value={(totals.sponsorClicks || 0).toLocaleString()}
              detail="Clicked through to sponsor sites"
            />
          </div>

          {partnerExposure.pitchLines?.length > 0 && (
            <div className="owner-dashboard__meet-pitch-lines">
              <p className="owner-dashboard__meet-pitch-lines-label">Talking points</p>
              <ul className="owner-dashboard__meet-pitch-lines-list">
                {partnerExposure.pitchLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          {partnerExposure.topPartners?.length > 0 && (
            <div className="owner-dashboard__meet-block owner-dashboard__meet-block--nested">
              <h5 className="owner-dashboard__meet-block-title">Top local listings</h5>
              <p className="owner-dashboard__meet-block-note">
                Hotels and restaurants — clicks on their card or directions link.
              </p>
              <BarList rows={partnerExposure.topPartners} countLabel="clicks" />
            </div>
          )}

          {partnerExposure.tourismByCategory?.length > 0 && (
            <div className="owner-dashboard__meet-block owner-dashboard__meet-block--nested">
              <h5 className="owner-dashboard__meet-block-title">What they clicked</h5>
              <BarList rows={partnerExposure.tourismByCategory} countLabel="clicks" />
            </div>
          )}

          {partnerExposure.topSponsors?.length > 0 && (
            <div className="owner-dashboard__meet-block owner-dashboard__meet-block--nested">
              <h5 className="owner-dashboard__meet-block-title">Sponsor exposure</h5>
              <p className="owner-dashboard__meet-block-note">
                Logo clicks on the meet page — share with current sponsors or prospects.
              </p>
              <BarList rows={partnerExposure.topSponsors} countLabel="clicks" />
            </div>
          )}
        </div>
      )}

      {registerByLocation?.length > 0 && (
        <div className="owner-dashboard__meet-block">
          <h4 className="owner-dashboard__meet-block-title">Where register clicks came from</h4>
          <p className="owner-dashboard__meet-block-note">
            Which button on the meet page drove sign-up intent.
          </p>
          <BarList rows={registerByLocation} countLabel="clicks" />
        </div>
      )}

      {sectionsViewed?.length > 0 && (
        <div className="owner-dashboard__meet-block">
          <h4 className="owner-dashboard__meet-block-title">Sections people read</h4>
          <p className="owner-dashboard__meet-block-note">
            Fired when a section scrolls into view — shows how far down the page families get.
          </p>
          <BarList rows={sectionsViewed.slice(0, 8)} countLabel="views" />
        </div>
      )}

      {sectionNav?.length > 0 && (
        <div className="owner-dashboard__meet-block">
          <h4 className="owner-dashboard__meet-block-title">Section menu clicks</h4>
          <p className="owner-dashboard__meet-block-note">
            Jump links at the top of the page — what people skipped to directly.
          </p>
          <BarList rows={sectionNav.slice(0, 6)} countLabel="clicks" />
        </div>
      )}

      {topQuestions?.length > 0 && (
        <div className="owner-dashboard__meet-block">
          <h4 className="owner-dashboard__meet-block-title">Top FAQ questions opened</h4>
          <BarList
            rows={topQuestions.map((row) => ({
              ...row,
              friendlyLabel: row.friendlyLabel || row.label,
            }))}
            countLabel="opens"
          />
        </div>
      )}

      {actions?.length > 0 && (
        <div className="owner-dashboard__meet-block">
          <h4 className="owner-dashboard__meet-block-title">All meet page actions</h4>
          <p className="owner-dashboard__meet-block-note">
            Every tracked interaction on the meet page in this period.
          </p>
          <BarList
            rows={actions.map((row) => ({
              label: row.id,
              friendlyLabel: row.label,
              count: row.count,
              sharePercent: row.sharePercent,
            }))}
            countLabel="actions"
          />
        </div>
      )}
    </article>
  );
};

export default MeetPageInsights;
